import { useParams, useNavigate } from "react-router";
import { useQuery, useMutation } from "@apollo/client/react";
import { GQLQuery, GQLMutation } from "@rx-cs-system/schema";
import { useHandleError } from "../../../hooks";
import {
  GET_MEDICAL_INSTITUTION,
  GET_MEDICAL_INSTITUTION_MEMBERS_BY_MEDICAL_INSTITUITON_ID,
  GET_CONTRACTS_INCLUDING_CANCELED,
} from "../../../graphql/queries";
import {
  CANCEL_CONTRACT_MUTATION,
  CANCEL_MEDICAL_INSTITUTION_MUTATION,
} from "../../../graphql/mutations";
import {
  parseMembers,
  parseContracts,
  parseEmailContactSummary,
} from "../../../graphql/utils";
import {
  MedicalInstitutionDetailPage as Component,
  MedicalInstitutionProps,
} from "../../../components/pages/medicalInstitution/MedicalInstitutionDetailPage";
import userIcon from "../../../assets/images/users/sample.png";
import { AuthContext } from "../../../hooks/useAuthentication";
import { use } from "react";

export const MedicalInstitutionDetailPage = () => {
  const navigate = useNavigate();
  const { auth } = use(AuthContext);
  const { id: medicalInstitutionId } = useParams<{ id: string }>();
  if (!medicalInstitutionId)
    throw new Error("medicalInstitutionId is undefined");

  const getMedicalInstitutionPayload = useQuery<GQLQuery>(
    GET_MEDICAL_INSTITUTION,
    {
      variables: { medicalInstitutionId },
    },
  );

  useHandleError(getMedicalInstitutionPayload.error);

  const medicalInstitutionDetail =
    getMedicalInstitutionPayload.data?.medicalInstitutionDetail;

  const contracts = parseContracts(
    getMedicalInstitutionPayload.data?.contracts || [],
  );
  const emailContracts = (
    getMedicalInstitutionPayload.data?.emailContacts || []
  ).map(parseEmailContactSummary);

  const getMedicalInstitutionMembersPayload = useQuery<GQLQuery>(
    GET_MEDICAL_INSTITUTION_MEMBERS_BY_MEDICAL_INSTITUITON_ID,
    {
      variables: { medicalInstitutionId },
    },
  );

  useHandleError(getMedicalInstitutionMembersPayload.error);

  const getContractsIncludingCanceledPayload = useQuery<GQLQuery>(
    GET_CONTRACTS_INCLUDING_CANCELED,
    {
      variables: { medicalInstitutionId },
    },
  );

  useHandleError(getContractsIncludingCanceledPayload.error);

  const [cancelContractMutation] = useMutation<GQLMutation>(
    CANCEL_CONTRACT_MUTATION,
  );

  const [cancelMedicalInstitutionMutation] = useMutation<GQLMutation>(
    CANCEL_MEDICAL_INSTITUTION_MUTATION,
  );

  const medicalInstitutionMembers =
    getMedicalInstitutionMembersPayload.data
      ?.medicalInstitutionMembersByMedicalInstitutionId;

  const contractsIncludingCanceled = (
    getContractsIncludingCanceledPayload.data?.contractsIncludingCanceled || []
  ).map((c) => ({
    id: c.id,
    pdt: {
      id: c.pdt.id,
      name: c.pdt.name,
      iconPath: `/${c.pdt.iconPath}`,
    },
    createdAt: c.createdAt,
    deletedAt: c.deletedAt,
  }));

  const allContractsCanceled = contractsIncludingCanceled.every(
    (c) => c.deletedAt != null,
  );

  if (!medicalInstitutionDetail || !medicalInstitutionMembers) {
    return null;
  }

  const handleCancelContract = async (contractId: string) => {
    const contract = contractsIncludingCanceled.find(
      (c) => c.id === contractId,
    );
    const pdtName = contract?.pdt.name ?? "";
    const miName = medicalInstitutionDetail.name;
    if (
      !confirm(
        `「${miName}」の「${pdtName}」を解約しますか？\n解約すると元に戻せません。`,
      )
    ) {
      return;
    }
    try {
      await cancelContractMutation({
        variables: { input: { contractId } },
      });
      await getContractsIncludingCanceledPayload.refetch();
    } catch (e) {
      alert("解約処理に失敗しました。");
    }
  };

  const handleCancelMedicalInstitution = async () => {
    const miName = medicalInstitutionDetail.name;
    if (
      !confirm(
        `「${miName}」をAPSから退会させますか？\n退会すると元に戻せません。`,
      )
    ) {
      return;
    }
    try {
      const { data } = await cancelMedicalInstitutionMutation({
        variables: { input: { medicalInstitutionId } },
      });

      const result = data?.cancelMedicalInstitution;
      if (
        result &&
        "code" in result &&
        result.code === "ACTIVE_CONTRACT_EXISTS"
      ) {
        alert(
          `有効な製品契約が${result.activeContractCount}件あるため、退会できません。\n先に全ての製品契約を解約してください。`,
        );
        return;
      }

      alert("退会処理が完了しました。");
      await getMedicalInstitutionPayload.refetch();
      await getContractsIncludingCanceledPayload.refetch();
    } catch (e) {
      alert("退会処理に失敗しました。");
    }
  };

  // 再送画面は URL のパラメータから自分で契約情報を取得するため、ここでは遷移のみ行う
  const handleContractResendWelcomeMail = async (contractId: string) => {
    const contract = contracts.find((c) => c.id === contractId);

    if (!contract || !contract.manager) {
      return;
    }

    navigate(
      `/medicalInstitution/${medicalInstitutionId}/contracts/${contractId}/resendWelcomeMail`,
    );
  };

  const handleResponsiblePersonResendWelcomeMail = async (
    contractId: string,
  ) => {
    const contract = contracts.find((c) => c.id === contractId);

    if (!contract || !contract.responsiblePerson) {
      return;
    }

    navigate(
      `/medicalInstitution/${medicalInstitutionId}/contracts/${contractId}/resendWelcomeMailV2`,
    );
  };

  const refetchMedicalInstitutionMembers = () => {
    getMedicalInstitutionMembersPayload.refetch();
  };

  const props: MedicalInstitutionProps = {
    userData: auth,
    medicalInstitution: {
      ...medicalInstitutionDetail,
      memberImage: userIcon,
      apsUrl: `https://${medicalInstitutionDetail.clientCertificate.domain}`,
      billingAccount: medicalInstitutionDetail.billingAccount,
    },
    clientCertificate: medicalInstitutionDetail.clientCertificate,
    medicalInstitutionMembers: parseMembers(medicalInstitutionMembers),
    contracts,
    contractsIncludingCanceled,
    emailContracts,
    onCancelContract: handleCancelContract,
    onCancelMedicalInstitution: handleCancelMedicalInstitution,
    allContractsCanceled,
    refetchMedicalInstitutionMembers,
    onContractManagerResendWelcomeMailButtonClick:
      handleContractResendWelcomeMail,
    onResponsiblePersonResendWelcomeMailButtonClick:
      handleResponsiblePersonResendWelcomeMail,
  };

  return <Component {...props} />;
};
